import { useRef, useEffect, useCallback } from 'react'
import { useMaiaStore } from '../core/stores/maiaStore'

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
    : null

const WAKE_PHRASES = [
  'elettra ascolta',
  'elettra, ascolta',
  'elettra ascoltami',
  'elettra, ascoltami',
]

interface UseWakeWordOptions {
  enabled: boolean
  onWake: () => void
}

export function useWakeWord({ enabled, onWake }: UseWakeWordOptions): void {
  const recognitionRef = useRef<any>(null)
  const enabledRef = useRef(enabled)
  const onWakeRef = useRef(onWake)
  const activeRef = useRef(false)
  // Flag: wake word rilevato, in attesa che Chrome rilasci il mic (onend)
  const wakeDetectedRef = useRef(false)

  enabledRef.current = enabled
  onWakeRef.current = onWake

  const setActive = useCallback((on: boolean) => {
    activeRef.current = on
    useMaiaStore.getState().setWakeWordActive(on)
  }, [])

  const startListener = useCallback(() => {
    if (!SpeechRecognitionAPI) return
    if (!enabledRef.current) return

    // Kill any existing instance first (idempotente)
    if (recognitionRef.current) {
      try { recognitionRef.current.abort() } catch {}
      recognitionRef.current = null
    }
    wakeDetectedRef.current = false

    const recognition = new SpeechRecognitionAPI()
    recognition.lang = 'it-IT'
    recognition.continuous = true
    recognition.interimResults = false
    recognition.maxAlternatives = 3

    recognition.onstart = () => {
      setActive(true)
    }

    recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (!result.isFinal) continue

        // Controlla tutte le alternative per le wake phrases
        for (let j = 0; j < result.length; j++) {
          const transcript = result[j].transcript.toLowerCase().trim()
          for (const phrase of WAKE_PHRASES) {
            if (transcript.includes(phrase)) {
              // Wake word rilevato! Segnala e chiudi — NON chiamare onWake qui
              // onWake verrà chiamato in onend, quando Chrome ha rilasciato il mic
              wakeDetectedRef.current = true
              if (recognitionRef.current) {
                try { recognitionRef.current.abort() } catch {}
                recognitionRef.current = null
              }
              setActive(false)
              return
            }
          }
        }
      }
    }

    recognition.onerror = (event: any) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn('[ELETTRA WakeWord] Error:', event.error)
      }
      setActive(false)
    }

    recognition.onend = () => {
      recognitionRef.current = null
      setActive(false)

      // Se il wake word era stato rilevato, ORA il mic è libero → chiama onWake
      // IMPORTANTE: NON resettare wakeDetectedRef qui — lo fa stopListener() quando enabled diventa false.
      // Se lo resettassimo qui, il health check (3s) potrebbe riavviare il wake listener
      // PRIMA che la dettatura abbia avuto tempo di partire, rubando il mic.
      if (wakeDetectedRef.current) {
        // Delay extra: Chrome conferma la chiusura ma il driver audio potrebbe avere un lag
        setTimeout(() => onWakeRef.current(), 500)
      }
    }

    recognitionRef.current = recognition
    try {
      recognition.start()
    } catch (e) {
      console.warn('[ELETTRA WakeWord] Start failed:', e)
      recognitionRef.current = null
      setActive(false)
    }
  }, [setActive])

  const stopListener = useCallback(() => {
    wakeDetectedRef.current = false
    if (recognitionRef.current) {
      try { recognitionRef.current.abort() } catch {}
      recognitionRef.current = null
    }
    setActive(false)
  }, [setActive])

  // Start/stop basato su enabled
  useEffect(() => {
    if (enabled) {
      const delay = setTimeout(() => startListener(), 600)
      return () => clearTimeout(delay)
    } else {
      stopListener()
    }
  }, [enabled, startListener, stopListener])

  // Health-check: se dovrebbe essere attivo ma non lo è, riavvia
  useEffect(() => {
    if (!enabled) return

    const interval = setInterval(() => {
      if (enabledRef.current && !activeRef.current && !recognitionRef.current && !wakeDetectedRef.current) {
        startListener()
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [enabled, startListener])

  // Cleanup on unmount
  useEffect(() => {
    return () => stopListener()
  }, [stopListener])
}
